import View              from '@widesk-ui/components/View';
import FileUpload        from '@widesk-ui/components/form/FileUpload';
import { useState }      from 'react';
import Img               from '@widesk-ui/components/Img';
import Values            from '@widesk-ui/components/Values';
import { CloseOutlined } from '@ant-design/icons';
import Button            from '@widesk-ui/components/Button';

type Extension = 'jpg' | 'png' | 'webp' | 'svg' | 'gif';
const mimeTypeByExtension: Record<Extension, `image/${string}`> = {
	gif: 'image/gif',
	png: 'image/png',
	svg: 'image/svg+xml',
	webp: 'image/webp',
	jpg: 'image/jpeg',
};

/**
 * Can be loaded with Form.setFieldsValue() using :
 * - 'url' for public images.
 * - 'src' for private images, by converting to base64 then to string.
 * On submit, use 'file' to POST.
 */
export default function FileInput({
	aspect = '1/1',
	disableAspectCheck,
	value,
	onChange,
	minSize,
	extensions,
	inputMaxWidth,
	clearDisabled,
}: {
	aspect?: `${number}/${number}` | 'free';
	disableAspectCheck?: boolean,
	extensions?: Extension[];
	minSize?: [number, number];
	value?: FileInputValue;
	onChange?: (value?: FileInputValue) => void;
	inputMaxWidth?: number;
	clearDisabled?: boolean;
}) {
	const [isHover, setIsHover] = useState(false);

	const url = value?.url;
	const src = value?.src;
	const imageSrc = url || src || undefined;

	return (
		<View gap="xs" maxW={inputMaxWidth}>
			<View size={10}>
				<Values
					columns={[
						[
							{
								hidden: !extensions,
								label: 'Extension',
								value: extensions,
							},
							{
								hidden: aspect === 'free' || disableAspectCheck,
								label: `Format d'image`,
								value: aspect === '1/1' ? 'carré (1/1)' : aspect,
							},
							{
								hidden: !minSize,
								label: `Dimensions minimales`,
								value: `${minSize?.[0]}L x ${minSize?.[1]}H`,
							},
						],
					]}
				/>
			</View>

			<View aspectRatio={aspect && aspect !== 'free' ? eval(aspect) : undefined} onHover={setIsHover} rel>
				<FileUpload
					imageRequirements={{
						aspect: (aspect === 'free' || disableAspectCheck) ? undefined : aspect,
						minDimensions: { width: minSize?.[0] || 200, height: minSize?.[1] || 200 },
					}}
					mimeTypeReferences={extensions?.map(extension => mimeTypeByExtension[extension])}
					hideRequirements
					onFileListChange={(fileList) => {
						const file = fileList[0]!;

						setIsHover(false);

						onChange?.({ url: URL.createObjectURL(file), file });
					}}
					showImgEditor={aspect !== 'free' && !disableAspectCheck}
				>
					{!!imageSrc && (
						<>
							<View absT={0} absL={0} centerV heightF hidden={isHover} widthF bg="white">
								<Img
									src={imageSrc}
									preview={false}
									style={{ height: '100%', objectFit: 'contain' }}
									wrapperStyle={{ height: '100%' }}
								/>
							</View>

							{!clearDisabled && (
								<View absT={4} absR={4} onHover={v => setIsHover(!v)}>
									<Button size="small" onClick={e => {
										e.stopPropagation();
										onChange?.(undefined);
									}}>
										<CloseOutlined />
									</Button>
								</View>
							)}
						</>
					)}
				</FileUpload>
			</View>
		</View>
	);
}